import type { AdminMoneyHealthDto } from '@carcheck/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';
import { isNonRetryableStatus } from './errors';

/**
 * `GET /admin/money-health` — the whole «Нужно внимание» tab in one read
 * (AUT-328). Wire spec + every rule the payload encodes:
 * `packages/shared/src/wire/admin-money-health.ts`.
 *
 * No query parameters exist: thresholds, caps and truncation flags are all in
 * the response, so there is nothing to tune from here.
 *
 * ## Three deliberate divergences from the other admin reads
 *
 * **1. `skipAutoErrorToast: true` (the AUT-223 precedent).** The shared
 * interceptor toasts on every 403, and the toast bus does not dedupe. On a
 * polled query that is a toast every 30 seconds forever for an operator whose
 * `process_payouts` was revoked mid-session (the JWT-derived permission set is
 * up to 15 minutes stale, so `RequirePermission` still lets the page mount).
 * The page renders that 403 as an inline state instead.
 *
 * **2. The poll halts on a non-retryable status.** Same reason: nothing about
 * re-issuing a 403 or a 404 makes it succeed. 5xx and network errors keep
 * polling — the API restarting is exactly what a watch tab should ride out.
 *
 * **3. No `staleTime` override.** The global 30s matches the poll cadence, so a
 * focus-refetch doesn't double-fire (queryClient.ts).
 *
 * TanStack keeps the last successful `data` through a failed refetch, and the
 * page relies on it: a blip must not replace four sections with four error
 * alerts. It renders the last-good payload under one staleness banner.
 */

const MONEY_HEALTH_BASE = '/admin/money-health';

/** Stable query-key (`['admin', '<resource>']` convention). */
export const ADMIN_MONEY_HEALTH_KEY = ['admin', 'money-health'] as const;

/**
 * Poll cadence (ms). Matches `/health` + queue-counts so the panel has one
 * heartbeat, and it is rendered on screen — a watch tab that doesn't say how
 * fresh it is has the defect it exists to fix.
 */
export const MONEY_HEALTH_REFETCH_MS = 30_000;

/**
 * Poll cadence given the query's last error. `false` HALTS the poll — see
 * divergence 2. Named (not inline) so the halt is a unit test rather than a
 * 30-second wait.
 */
export const moneyHealthPollInterval = (error: unknown): number | false =>
  isNonRetryableStatus(error) ? false : MONEY_HEALTH_REFETCH_MS;

/** One silent retry on a transient failure; none on a stated 4xx. */
export const moneyHealthShouldRetry = (failureCount: number, error: unknown): boolean =>
  !isNonRetryableStatus(error) && failureCount < 1;

export const useAdminMoneyHealth = (): UseQueryResult<AdminMoneyHealthDto, Error> =>
  useQuery({
    queryKey: ADMIN_MONEY_HEALTH_KEY,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminMoneyHealthDto>(MONEY_HEALTH_BASE, {
        signal,
        skipAutoErrorToast: true,
      });
      return data;
    },
    refetchInterval: (query) => moneyHealthPollInterval(query.state.error),
    retry: (failureCount, error) => moneyHealthShouldRetry(failureCount, error),
  });
